import React, { useState, useEffect } from 'react'
import { useApi } from '../contexts/ApiContext'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  PieChart
} from 'lucide-react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'

// Cores validadas para o par "período atual" x "mesmo período ano anterior"
const COR_ATUAL = '#2a78d6'
const COR_ANTERIOR = '#eb6834'

// Janelas cumulativas do índice de inadimplência (chaves batem com o retorno do backend)
const PERIODOS_INADIMPLENCIA = [
  { chave: '1', label: '1 ano', campoVendido: 'valor_vendido_1_ano', campoVencido: 'valor_vencido_1_ano', campoIndice: 'indice_1_ano' },
  { chave: '2', label: '2 anos', campoVendido: 'valor_vendido_2_anos', campoVencido: 'valor_vencido_2_anos', campoIndice: 'indice_2_anos' },
  { chave: '3', label: '3 anos', campoVendido: 'valor_vendido_3_anos', campoVencido: 'valor_vencido_3_anos', campoIndice: 'indice_3_anos' }
]

// Limiares de severidade do índice de inadimplência (%)
const LIMIAR_ATENCAO = 5
const LIMIAR_CRITICO = 15

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)

const formatCompact = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value || 0)

function BadgeCrescimento({ valor }) {
  const positivo = valor >= 0
  const Icon = positivo ? TrendingUp : TrendingDown
  return (
    <span
      className={`inline-flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${
        positivo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {positivo ? '+' : ''}
      {valor.toFixed(1)}%
    </span>
  )
}

function TooltipGrafico({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      {payload.map((item) => (
        <p key={item.dataKey} style={{ color: item.color }}>
          {item.name}: {formatCurrency(item.value)}
        </p>
      ))}
    </div>
  )
}

function TooltipIndice({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      <p style={{ color: payload[0].color }}>Índice de inadimplência: {payload[0].value.toFixed(1)}%</p>
    </div>
  )
}

function classificarIndice(valor) {
  if (valor >= LIMIAR_CRITICO) return 'critico'
  if (valor >= LIMIAR_ATENCAO) return 'atencao'
  return 'bom'
}

const CORES_INDICE = {
  bom: '#0ca30c',
  atencao: '#fab219',
  critico: '#d03b3b'
}

function BadgeIndice({ valor }) {
  const nivel = classificarIndice(valor)
  const estilos = {
    bom: 'bg-green-100 text-green-700',
    atencao: 'bg-amber-100 text-amber-700',
    critico: 'bg-red-100 text-red-700'
  }
  const Icon = nivel === 'bom' ? CheckCircle2 : AlertTriangle
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${estilos[nivel]}`}>
      <Icon className="w-3.5 h-3.5" />
      {valor.toFixed(1)}%
    </span>
  )
}

export default function DashboardAdmin() {
  const { getVendasMesVendedor, getIndiceInadimplencia } = useApi()
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [dadosInadimplencia, setDadosInadimplencia] = useState(null)
  const [loadingInadimplencia, setLoadingInadimplencia] = useState(true)
  const [errorInadimplencia, setErrorInadimplencia] = useState(null)
  const [periodoInadimplencia, setPeriodoInadimplencia] = useState('1')

  useEffect(() => {
    carregar()
    carregarInadimplencia()
  }, [])

  const carregar = async () => {
    try {
      setLoading(true)
      setError(null)
      const resposta = await getVendasMesVendedor()
      setDados(resposta.data || resposta)
    } catch (err) {
      console.error('Erro ao carregar vendas do mês por vendedor:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const carregarInadimplencia = async () => {
    try {
      setLoadingInadimplencia(true)
      setErrorInadimplencia(null)
      const resposta = await getIndiceInadimplencia()
      setDadosInadimplencia(resposta.data || resposta)
    } catch (err) {
      console.error('Erro ao carregar índice de inadimplência:', err)
      setErrorInadimplencia(err.message)
    } finally {
      setLoadingInadimplencia(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Carregando dashboard administrativo..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-gray-600 text-lg mb-4">Erro ao carregar dados</p>
        <p className="text-gray-500 mb-6">{error}</p>
        <button
          onClick={carregar}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  const porVendedor = dados?.por_vendedor || []
  const crescimentoTotal = dados?.crescimento_percentual ?? 0
  const dadosGrafico = porVendedor.map((v) => ({
    nome: v.nome_vendedor,
    Atual: v.valor_atual,
    'Ano Anterior': v.valor_ano_anterior
  }))

  const periodoInad = PERIODOS_INADIMPLENCIA.find((p) => p.chave === periodoInadimplencia)
  const geralInadimplencia = dadosInadimplencia?.geral || null
  const porVendedorInadimplencia = [...(dadosInadimplencia?.por_vendedor || [])].sort(
    (a, b) => b[periodoInad.campoIndice] - a[periodoInad.campoIndice]
  )
  const dadosGraficoIndice = porVendedorInadimplencia.map((v) => ({
    nome: v.nome_vendedor,
    'Índice': v[periodoInad.campoIndice]
  }))

  return (
    <div className="space-y-6">
      {/* Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Dashboard Administrativo</h1>
            <p className="text-indigo-100 text-lg">
              Vendas do mês, comparadas ao mesmo período do ano anterior
            </p>
          </div>
          <div className="hidden lg:block">
            <BarChart3 className="w-24 h-24 text-indigo-300 opacity-50" />
          </div>
        </div>
      </div>

      {/* Cards de totais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-blue-100">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <BadgeCrescimento valor={crescimentoTotal} />
          </div>
          <p className="text-sm text-gray-600 mb-1">Total de Vendas (mês atual)</p>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(dados?.total_atual)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-orange-100">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-1">Mesmo período, ano anterior</p>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(dados?.total_ano_anterior)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-purple-100">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-1">Vendedores com vendas no período</p>
          <p className="text-3xl font-bold text-gray-900">{porVendedor.length}</p>
        </div>
      </div>

      {/* Gráfico por vendedor */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Vendas por Vendedor</h3>
        {dadosGrafico.length === 0 ? (
          <p className="text-gray-500 text-sm py-8 text-center">Nenhuma venda registrada no período.</p>
        ) : (
          <div className="w-full overflow-x-auto">
            <ResponsiveContainer width="100%" height={Math.max(320, dadosGrafico.length * 50)}>
              <BarChart data={dadosGrafico} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tickFormatter={formatCompact} stroke="#9ca3af" fontSize={12} />
                <YAxis type="category" dataKey="nome" width={140} stroke="#9ca3af" fontSize={12} />
                <Tooltip content={<TooltipGrafico />} />
                <Legend />
                <Bar dataKey="Atual" fill={COR_ATUAL} radius={[0, 4, 4, 0]} />
                <Bar dataKey="Ano Anterior" fill={COR_ANTERIOR} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tabela por vendedor */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalhamento por Vendedor</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">Vendedor</th>
                <th className="py-2 pr-4 font-medium text-right">Mês Atual</th>
                <th className="py-2 pr-4 font-medium text-right">Ano Anterior</th>
                <th className="py-2 pr-4 font-medium text-right">Variação</th>
              </tr>
            </thead>
            <tbody>
              {porVendedor.map((v) => (
                <tr key={v.ordem_vendedor} className="border-b border-gray-100 last:border-0">
                  <td className="py-3 pr-4 text-gray-900">{v.nome_vendedor}</td>
                  <td className="py-3 pr-4 text-right text-gray-900">{formatCurrency(v.valor_atual)}</td>
                  <td className="py-3 pr-4 text-right text-gray-500">{formatCurrency(v.valor_ano_anterior)}</td>
                  <td className="py-3 pr-4 text-right">
                    <BadgeCrescimento valor={v.crescimento_percentual} />
                  </td>
                </tr>
              ))}
              {porVendedor.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    Nenhuma venda registrada no período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Índice de Inadimplência */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Índice de Inadimplência</h3>
            <p className="text-sm text-gray-500">Valor vencido em aberto ÷ valor vendido a prazo (crediário/parcelado)</p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
            {PERIODOS_INADIMPLENCIA.map((p) => (
              <button
                key={p.chave}
                onClick={() => setPeriodoInadimplencia(p.chave)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  periodoInadimplencia === p.chave
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loadingInadimplencia ? (
          <div className="flex items-center justify-center h-48">
            <LoadingSpinner size="md" text="Carregando índice de inadimplência..." />
          </div>
        ) : errorInadimplencia ? (
          <div className="flex flex-col items-center justify-center h-48">
            <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
            <p className="text-gray-500 mb-4 text-sm">{errorInadimplencia}</p>
            <button
              onClick={carregarInadimplencia}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            {/* Cards do índice geral */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="p-3 rounded-lg bg-blue-100 w-fit mb-4">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Vendido a prazo ({periodoInad.label})</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(geralInadimplencia?.[periodoInad.campoVendido])}
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="p-3 rounded-lg bg-orange-100 w-fit mb-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Vencido em aberto ({periodoInad.label})</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(geralInadimplencia?.[periodoInad.campoVencido])}
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="p-3 rounded-lg bg-purple-100 w-fit mb-4">
                  <PieChart className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Índice geral ({periodoInad.label})</p>
                <div className="mt-1">
                  <BadgeIndice valor={geralInadimplencia?.[periodoInad.campoIndice] ?? 0} />
                </div>
              </div>
            </div>

            {/* Gráfico por vendedor */}
            {dadosGraficoIndice.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">Nenhum dado de inadimplência no período.</p>
            ) : (
              <div className="w-full overflow-x-auto mb-6">
                <ResponsiveContainer width="100%" height={Math.max(320, dadosGraficoIndice.length * 50)}>
                  <BarChart data={dadosGraficoIndice} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}%`} stroke="#9ca3af" fontSize={12} />
                    <YAxis type="category" dataKey="nome" width={140} stroke="#9ca3af" fontSize={12} />
                    <Tooltip content={<TooltipIndice />} />
                    <Bar dataKey="Índice" radius={[0, 4, 4, 0]}>
                      {dadosGraficoIndice.map((entry, index) => (
                        <Cell key={index} fill={CORES_INDICE[classificarIndice(entry['Índice'])]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Tabela por vendedor */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Vendedor</th>
                    <th className="py-2 pr-4 font-medium text-right">Vendido a Prazo</th>
                    <th className="py-2 pr-4 font-medium text-right">Vencido em Aberto</th>
                    <th className="py-2 pr-4 font-medium text-right">Índice</th>
                  </tr>
                </thead>
                <tbody>
                  {porVendedorInadimplencia.map((v) => (
                    <tr key={v.codigo_vendedor} className="border-b border-gray-100 last:border-0">
                      <td className="py-3 pr-4 text-gray-900">{v.nome_vendedor}</td>
                      <td className="py-3 pr-4 text-right text-gray-900">
                        {formatCurrency(v[periodoInad.campoVendido])}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-500">
                        {formatCurrency(v[periodoInad.campoVencido])}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <BadgeIndice valor={v[periodoInad.campoIndice]} />
                      </td>
                    </tr>
                  ))}
                  {porVendedorInadimplencia.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500">
                        Nenhum dado de inadimplência no período.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
